import classes from "./newJob.module.css";
import { useCallback, useContext, useEffect, useState } from "react";
import JobPopup from "../../../components/NewJob/Popup/popup";
import {
  clientAddNewJob,
  clientGetRoles,
  clientMatchRandomTalent,
  clientUpdateJob,
  findClientSubscription,
  getClientJobById,
  matchTalent,
  previewRoleMatchCost,
  postSubscription,
  subscriptionHistory,
} from "../../../services/clientCalls";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DEFAULT_CLIENT_SUBSCRIPTION } from "../../../constants";
import {
  describeMatchCost,
  quoteFromResponse,
  errorHandler,
  isPlanLimitError,
  planLimitHandler,
  toastSuccess,
} from "../../../utils";
import SubPopup from "../../../components/NewJob/SubPopup/popup";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import ErrorModal from "../../../layout/ErrorModal/ErrorModal";
import confusedStan from "../../../assets/illustrations/no-sub-stan.png";
import JobTemplatePopup from "../../../components/JobTemplatePopup/jobTemplatePopup";
import JobForm from "./JobForm";
import { IconBolt, IconBriefcase } from "@tabler/icons-react";
import PageTabs from "./PageTabs";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import { talentRoles } from "../../../services/talentCalls";
import CustomModal from "../../../components/Modal/CustomModal";
import moment from "moment";

const AddNewJob = ({ jobType = "Job" }) => {
  let navigate = useNavigate();
  const [modal, showModal] = useState(false);
  const [load, setLoad] = useState(false);
  const [subscription, setSubscription] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [jobPostSuccessful, setJobPostSuccessful] = useState(true);
  const [subscribeIsLoading, setSubscribeIsLoading] = useState(false);
  const [subPopModal, setSubPopModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editID, setEditID] = useState("");
  const [editData, setEditData] = useState(null);
  const [param] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [talentId, setTalentId] = useState(param.get("talentId") || "");
  const [matchQuote, setMatchQuote] = useState(null);
  const [jobTemplate, setJobTemplate] = useState(null);
  const [locationValue, setLocationValue] = useState(null);
  const [allTalentRoles, setAllTalentRoles] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(true);
  const [selectedPlanName, setSelectedPlanName] = useState(
    DEFAULT_CLIENT_SUBSCRIPTION,
  ); // TODO: default value is Basic Plan, till new plans are added and passed from the subscription page

  const [jobChecks, setJobChecks] = useState([false, false, false]);
  const [jobNotice, setJobNotice] = useState({ show: false, text: "" });
  const { pathname } = useLocation();
  const [gigNotice, setGigNotice] = useState({ show: false, text: "" });
  const activeTab = pathname.endsWith("/gig") ? "gig" : "job";
  const [roleLoading, setRoleLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  const [data, setData] = useState({
    role: "",
    title: "",
    workMode: "",
    type: "",
    budget: "",
    description: "",
    location: "",
    objectives: "",
    acceptanceCriteria: "",
    startDate: "",
    // endDate: "2025-06-21T23:00:00.000Z",
    duration: "",
    skillsAndRequirements: [],
    language: "English",
    longTermJob: false,
    provideOneMonthNotice: false,
    hasNoRelocationPlans: false,
    jobType,
  });
  useEffect(() => {
    setData((prev) => ({
      ...prev,
      duration: "",
      jobType,
    }));
  }, [jobType]);

  const fetchSingleJob = async () => {
    try {
      setLoading(true);
      const res = await getClientJobById(editID);
      if (res?.data.data.job) {
        setLoading(false);
        setEditData(res?.data?.data?.job);
      }
    } catch (error) {
      setLoading(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleMatch = async (jobId) => {
    try {
      setLoad(true);
      const res = await clientMatchRandomTalent(jobId, talentId);
      if (res.data.success) {
        toastSuccess("Match Successful", () => navigate("/client-jobs"));
      }

      setLoad(false);
    } catch (error) {
      setLoad(false);
      if (isPlanLimitError(error)) return planLimitHandler(error, navigate);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error", () =>
            navigate(-1),
          )
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  useEffect(() => {
    if (param.get("edit") && param.get("id")) {
      setIsEdit(true);
      setEditID(param.get("id"));
    }
  }, [param]);

  useEffect(() => {
    if (editID) {
      fetchSingleJob();
    }
  }, [editID]);

  useEffect(() => {
    if (!talentId || !data.role) {
      setMatchQuote(null);
      return;
    }
    let current = true;
    previewRoleMatchCost(data.role)
      .then((res) => {
        if (current) {
          const quote = quoteFromResponse(res);
          if (quote) setMatchQuote(quote);
        }
      })
      .catch(() => {
        if (current) setMatchQuote(null);
      });
    return () => {
      current = false;
    };
  }, [talentId, data.role]);

  useEffect(() => {
    if (editData) {
      setData({
        ...data,
        role: editData?.roleId,
        title: editData?.title ?? "",
        workMode: editData?.workMode ?? "",
        type: editData?.type ?? "",
        budget: editData?.budget ?? "",
        description: editData?.description ?? "",
        duration: editData?.duration ?? "",
        location: editData?.location ?? "",
        objectives: editData?.objectives ?? "",
        acceptanceCriteria: editData?.acceptanceCriteria ?? "",
        startDate: moment(editData?.startDate).format("YYYY-MM-DD") ?? "",
        skillsAndRequirements: editData?.skillsAndRequirements ?? [],
        language: editData?.language ?? "English",
        longTermJob: editData?.longTermJob === "Yes",
        provideOneMonthNotice: editData?.provideOneMonthNotice === "Yes",
        hasNoRelocationPlans: editData?.hasNoRelocationPlans === "Yes",
        jobType: editData?.jobType ?? "Job",
        status: editData?.status ?? "Active",
      });

      if (editData?.status?.toLowerCase() === "draft") {
        setIsDraft(true);
      }

      setLocationValue(
        editData?.location
          ? {
              label: editData.location,
              value: {
                description: editData.location,
              },
            }
          : null,
      );
    }
  }, [editData]);

  useEffect(() => {
    if (jobTemplate) {
      setData({ ...data, ...jobTemplate });
    }
  }, [jobTemplate]);

  const handleClick = async (e) => {
    e.preventDefault();
    setLoad(true);

    try {
      const payload = {
        ...data,
        longTermJob: data.longTermJob ? "Yes" : "No",
        provideOneMonthNotice: data.provideOneMonthNotice ? "Yes" : "No",
        hasNoRelocationPlans: data.hasNoRelocationPlans ? "Yes" : "No",
        status: "Active"
      };
      const res = await (isEdit
        ? clientUpdateJob(editID, payload)
        : clientAddNewJob(payload));

      if (talentId && res?.data?.success && res?.data?.data?.job?._id)
        await handleMatch(res?.data?.data?.job?._id);

      if (res?.data?.success) {
        setIsDraft(false);
        setJobPostSuccessful(true);
        showModal(true);
        setTimeout(() => {
          showModal(false);
          navigate("/client-jobs");
        }, 2500);
      }
    } catch (error) {
      if (isPlanLimitError(error)) return planLimitHandler(error, navigate);
      const errorMessage =
        error?.response?.data?.errors ||
        "Network error: Something went wrong, Please check your internet connection and try again.";

      errorHandler(errorMessage, "Error");
    } finally {
      setLoad(false);
    }
  };

  const handleSaveDraft = async (e) => {
    try {
      setDraftLoading(true);

      // Ensure the updated data is passed to the API call
      const updatedData = {
        ...data,
        status: "Draft",
        longTermJob: data.longTermJob ? "Yes" : "No",
        provideOneMonthNotice: data.provideOneMonthNotice ? "Yes" : "No",
        hasNoRelocationPlans: data.hasNoRelocationPlans ? "Yes" : "No",
      };

      const res = await (isEdit
        ? clientUpdateJob(editID, updatedData)
        : clientAddNewJob(updatedData));
      setDraftLoading(false);
      setIsDraft(true);
      setJobPostSuccessful(true);
      showModal(true);

      setTimeout(() => {
        showModal(false);
        navigate("/client-jobs");
      }, 2500);
    } catch (error) {
      setDraftLoading(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const formatDurationInput = (value) => {
    // If the value is empty, return empty
    if (!value) return "";

    // Extract only the numeric part from the current value
    const numericValue = value.replace(/\D/g, "");

    // If no numeric value, return empty
    if (numericValue === "") return "";

    const num = parseInt(numericValue);

    // Return just the number if it's 0 or invalid
    if (num === 0 || isNaN(num)) return numericValue;

    if (jobType.toLowerCase() === "job") {
      // Format for jobs (years)
      if (num === 1) {
        return "1 year";
      } else if (num > 1) {
        return `${num} years`;
      }
    } else if (jobType.toLowerCase() === "gig") {
      // Format for gigs (months)
      if (num === 1) {
        return "1 month";
      } else if (num > 1) {
        return `${num} months`;
      }
    }

    return numericValue;
  };

  // Simple onChange that allows free typing
  const handleDurationChangeSimple = useCallback((e) => {
    const inputValue = e.target.value;
    setData((prevState) => ({
      ...prevState,
      duration: inputValue,
    }));
  }, []);

  // Handle formatting on blur
  const handleDurationBlur = useCallback((e) => {
    const inputValue = e.target.value;
    const formattedValue = formatDurationInput(inputValue);
    setData((prevState) => ({
      ...prevState,
      duration: formattedValue,
    }));
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await subscriptionHistory();

      if (res.data.data.records.length > 0) {
        // setSubscriptionID(res.data.data.records[0]._id)
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const fetchTalentRoles = async () => {
    setRoleLoading(true);
    try {
      const firstResponse = await clientGetRoles({ page: 1 });
      const firstData = firstResponse?.data?.data;

      if (!firstData) return;

      let allRecords = [...(firstData?.records ?? [])];

      for (let page = 2; page <= firstData?.totalPages; page++) {
        const response = await clientGetRoles({ page });
        const records = response?.data?.data?.records ?? [];
        allRecords = [...allRecords, ...records];
      }

      const sortedRoles = allRecords.sort((a, b) =>
        (a?.rolename ?? "").localeCompare(b?.rolename ?? ""),
      );

      setAllTalentRoles(sortedRoles);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchTalentRoles();
  }, []);

  const handleFindClientSubscription = async () => {
    try {
      const payload = {
        name: selectedPlanName,
      };
      if (selectedPlanName) {
        const res = await findClientSubscription(payload);
        const subscriptionId = res?.data?.data?.subscription?._id;
        if (subscriptionId) {
          return subscriptionId;
        }
      } else {
        errorHandler("Please select a plan", "Error");
        return null;
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleSubscription = useCallback(
    async (e) => {
      setSubscribeIsLoading(true);
      try {
        const subscriptionId = await handleFindClientSubscription();
        if (subscriptionId) {
          const res = await postSubscription(subscriptionId);
          if (res?.data?.data?.data?.data?.authorization_url) {
            window.location.href =
              res?.data?.data?.data?.data?.authorization_url;
          }
        }
      } catch (error) {
        setSubscribeIsLoading(false);
        error?.response?.data
          ? errorHandler(error?.response?.data?.errors, "Error")
          : errorHandler(
              "Network error: Something went wrong, Please check your internet connection and try again.",
              "Error",
            );
      } finally {
        setSubscribeIsLoading(false);
      }
    },
    [selectedPlanName],
  );

  // ── Field/tag/check handlers ──
  const toggleJobCheck = (i) =>
    setJobChecks((c) => c.map((v, idx) => (idx === i ? !v : v)));

  const tabs = [
    { id: "job", label: "Job", icon: <IconBriefcase size={17} /> },
    { id: "gig", label: "Gig", icon: <IconBolt size={17} /> },
  ];

  return (
    <div>
      {loading && <FullScreenLoader />}

      <Breadcrumb
        crumbs={[
          { label: "Dashboard", href: "/client-activity-summary" },
          { label: "My Jobs", href: "/client-jobs" },
          { label: isEdit ? "Update Job/Gig" : "Add New" },
        ]}
        title={isEdit ? "Update Job/Gig" : "Add a New Job/Gig"}
      />

      {!isEdit && <PageTabs tabs={tabs} />}

      {talentId && describeMatchCost(matchQuote) && (
        <p className="text-sm mb-4 text-[var(--text-primary)]">{describeMatchCost(matchQuote)}</p>
      )}

      <JobForm
        data={data}
        checks={jobChecks}
        onToggleCheck={toggleJobCheck}
        notice={jobNotice}
        onChangeTemplate={() => setShowTemplateModal(true)}
        onSaveDraft={handleSaveDraft}
        setData={setData}
        handleSubmit={handleClick}
        activeTab={activeTab}
        handleDurationChangeSimple={handleDurationChangeSimple}
        handleDurationBlur={handleDurationBlur}
        loading={load}
        locationValue={locationValue}
        setLocationValue={setLocationValue}
        allTalentRoles={allTalentRoles}
        roleLoading={roleLoading}
        draftLoading={draftLoading}
        isEdit={isEdit}
        isDraft={isDraft}
      />

      {modal && (
        <CustomModal
          state="success"
          title="Continue to Jobs Feed"
          description={
            isDraft
              ? "Your Gig/Job has been saved to your drafts, continue to jobs feed."
              : "Your Gig/Job has been successfully created, continue to jobs feed."
          }
          primaryLabel="Continue"
          onClose={() => {
            showModal(false);
            navigate("/client-jobs");
          }}
          onPrimaryAction={() => {
            showModal(false);
            navigate("/client-jobs");
          }}
        />
      )}
      {subPopModal && (
        <SubPopup
          showModal={showModal}
          onClick={() => handleSubscription()}
          subscribeIsLoading={subscribeIsLoading}
        />
      )}
      {!isEdit && (
        <JobTemplatePopup
          jobTemplate={jobTemplate}
          setJobTemplate={setJobTemplate}
          onClose={() => setShowTemplateModal(false)}
          open={showTemplateModal}
          title={`${jobType} template`}
          jobType={jobType}
          setJobNotice={setJobNotice}
        />
      )}

      <ErrorModal
        isModalOpen={subscription}
        setModalOpen={setSubscription}
        hasCloseIcon={true}
      >
        <div className={classes.modal}>
          <img className={classes.modalImage} src={confusedStan} alt="" />
          <h6>
            {" "}
            You are currently not <br /> on a payment plan
          </h6>
          <p>
            {" "}
            Click below to enable you publish your <br /> gig or job{" "}
          </p>
          <button
            onClick={() => {
              navigate("/subscription-payment");
              setSubscription(false);
            }}
            className={classes.modalButton}
          >
            Purchase Plan
          </button>
        </div>
      </ErrorModal>
    </div>
  );
};

export default AddNewJob;
